import { Children, useRef, useEffect, Fragment, cloneElement } from 'react';
import { useForkedRef, useGeneratedId, wrapEvent } from '../../../../utils/index.js';
import { KEYBOARD } from '../../../../constants/index.js';
import Popover from '../../../../popover/index.js';
import { useChildPropWarning } from './utils.js';
import { useTooltip } from '../TooltipProvider/useTooltip.js';
import { Content } from './styled.js';
import { jsx, jsxs } from 'react/jsx-runtime';

/**
 * Accessibility resource (unfinished): https://www.w3.org/TR/wai-aria-practices/#tooltip
 *
 * * Pressing ESC should close the tooltip
 * * Both mouseover and focus should show the tooltip
 * * "Tooltip widgets do not receive focus" (only the trigger)
 */
function Tooltip(props) {
  var unValidatedChildren = props.children,
    title = props.title,
    controlledId = props.id,
    disabled = props.disabled,
    width = props.width,
    _props$position = props.position,
    position = _props$position === void 0 ? 'bottom' : _props$position,
    _props$appearance = props.appearance,
    appearance = _props$appearance === void 0 ? 'primary' : _props$appearance;
  var children = Children.only(unValidatedChildren);
  var containerRef = useRef(null);
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore TS2339: Property 'ref' does not exist on type 'ReactElement  >'. Solve by finding a better type for "children"?
  var ref = useForkedRef(children.ref, containerRef);
  var autoId = useGeneratedId('tooltip');
  var id = controlledId || autoId;
  var _useTooltip = useTooltip(),
    activeTooltipId = _useTooltip.activeTooltipId,
    showTooltip = _useTooltip.showTooltip,
    hideTooltip = _useTooltip.hideTooltip;
  var open = activeTooltipId === id;
  var show = function show() {
    return showTooltip(id);
  };
  var hide = function hide() {
    return hideTooltip();
  };
  var _children$props = children.props,
    onMouseOver = _children$props.onMouseOver,
    onMouseLeave = _children$props.onMouseLeave,
    onFocus = _children$props.onFocus,
    onBlur = _children$props.onBlur,
    ariaDescribedBy = _children$props['aria-describedby'];
  useChildPropWarning(ariaDescribedBy);
  var handleMouseOver = wrapEvent(onMouseOver, show);
  var handleMouseLeave = wrapEvent(onMouseLeave, hide);
  var handleFocus = wrapEvent(onFocus, show);
  var handleBlur = wrapEvent(onBlur, hide);
  useEffect(function () {
    if (open && !disabled) {
      var handleKeyDown = function handleKeyDown(event) {
        if (event.key === KEYBOARD.ESC) {
          hideTooltip();
        }
      };
      document.addEventListener('keydown', handleKeyDown);
      return function () {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
    return undefined;
  }, [open, disabled, hideTooltip]);
  var triggerProps = {
    // rendering aria-describedby *only when open* ensures screen readers read the tooltip properly
    'aria-describedby': open ? id : undefined,
    onMouseOver: handleMouseOver,
    onMouseLeave: handleMouseLeave,
    onFocus: handleFocus,
    onBlur: handleBlur,
    ref: ref
  };
  if (disabled) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return /*#__PURE__*/jsx(Fragment, {
      children: children
    });
  }
  return /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/cloneElement(children, triggerProps), open && /*#__PURE__*/jsx(Popover, {
      targetRef: containerRef,
      position: position,
      children: /*#__PURE__*/jsx(Content, {
        role: "tooltip",
        id: id,
        $position: position,
        $appearance: appearance,
        $width: width,
        children: title
      })
    })]
  });
}

export { Tooltip };
